import { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { toArabicDigits } from '../../../utils/numbers';


const ContributionItem = ({
  contribution,
  depth = 0,
  onReply,
  onLikeComment,
  isAdmin,
  currentUserId,
  isAuthenticated,
  onDeleteComment,
  onEditComment,
  onUserClick,
  onSignupRedirect
}) => {
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [showMenu, setShowMenu] = useState(false);

  const isOwner = currentUserId && String(contribution?.userId) === String(currentUserId);
  const canModify = isOwner || isAdmin;

  const handleReplySubmit = () => {
    if (replyText?.trim()) {
      onReply?.(contribution?.id, replyText);
      setReplyText('');
      setShowReplyBox(false);
    }
  };

  return (
    <div className="relative">
      {/* Connecting Line */}
      {depth > 0 && (
        <div
          className="absolute top-0 right-0 w-[1px] h-full"
          style={{ backgroundColor: 'rgba(197, 160, 89, 0.15)' }}
        />
      )}

      <div
        className="mb-8"
        style={{
          marginRight: depth > 0 ? `${depth * 40}px` : '0',
          paddingRight: depth > 0 ? '20px' : '0'
        }}
      >
        {/* Contribution Content */}
        <div className="flex gap-4 items-start">
          {/* Avatar - Right side for RTL */}
          <Image
            src={contribution?.avatar}
            alt={contribution?.avatarAlt}
            className="w-12 h-12 rounded-full object-cover shadow-warm flex-shrink-0 cursor-pointer"
            type="user"
            onClick={() => contribution?.userId && onUserClick?.(contribution.userId)}
          />

          {/* Content */}
          <div className="flex-1">
            {/* Author & Timestamp */}
            <div className="flex items-center justify-between mb-2">
              <h4
                className="text-base font-bold cursor-pointer hover:text-[#C5A059] transition-colors"
                style={{ fontFamily: 'Amiri, serif', color: '#2C2B29' }}
                onClick={() => contribution?.userId && onUserClick?.(contribution.userId)}
              >
                {contribution?.author}
              </h4>
              <span
                className="text-sm"
                style={{ fontFamily: 'Amiri, serif', color: '#999' }}
              >
                {contribution?.timestamp}
              </span>
            </div>

            {/* Text */}
            {editingId === contribution?.id ? (
              <div className="mb-3">
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  placeholder="عدّل تعليقك..."
                  className="w-full p-3 rounded-xl bg-[#F9F7F2] border border-[rgba(197,160,89,0.3)] focus:outline-none focus:border-[rgba(197,160,89,0.6)] resize-none"
                  style={{
                    fontFamily: 'Amiri, serif',
                    fontSize: '16px',
                    minHeight: '80px',
                    direction: 'rtl'
                  }}
                  autoFocus
                />
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(null);
                      setEditText('');
                    }}
                    className="px-4 py-2 text-sm rounded-full transition-colors duration-300 hover:bg-[rgba(197,160,89,0.1)]"
                    style={{ fontFamily: 'Amiri, serif', color: '#999' }}
                  >
                    إلغاء
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (editText?.trim() && onEditComment) {
                        await onEditComment(contribution?.id, editText.trim());
                        setEditingId(null);
                        setEditText('');
                      }
                    }}
                    disabled={!editText?.trim()}
                    className="px-6 py-2 text-sm rounded-full bg-[#C5A059] text-white hover:bg-[#A68548] disabled:opacity-50 transition-colors duration-300"
                    style={{ fontFamily: 'Amiri, serif' }}
                  >
                    حفظ
                  </button>
                </div>
              </div>
            ) : (
              <p
                className="text-[18px] mb-3"
                style={{
                  fontFamily: 'Amiri, serif',
                  color: '#2C2B29',
                  lineHeight: '1.8',
                  direction: 'rtl'
                }}
              >
                {contribution?.text}
              </p>
            )}

            {/* Actions */}
            <div className="flex items-center gap-4">
              {onLikeComment && (
                <button
                  type="button"
                  onClick={() => onLikeComment(contribution?.id, contribution?.hasUserLiked)}
                  className="flex items-center gap-1 text-sm transition-colors duration-300 hover:text-[#C5A059]"
                  style={{ fontFamily: 'Amiri, serif', color: contribution?.hasUserLiked ? '#C5A059' : '#999' }}
                >
                  <Icon
                    name="Heart"
                    size={14}
                    color={contribution?.hasUserLiked ? '#C5A059' : '#999'}
                    fill={contribution?.hasUserLiked ? '#C5A059' : 'none'}
                  />
                  إعجاب ({toArabicDigits(contribution?.likeCount ?? 0)})
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (!isAuthenticated && onSignupRedirect) {
                    onSignupRedirect();
                  } else {
                    setShowReplyBox(!showReplyBox);
                  }
                }}
                className="text-sm transition-colors duration-300 hover:text-[#C5A059]"
                style={{ fontFamily: 'Amiri, serif', color: '#999' }}
              >
                رد
              </button>
              {canModify && editingId !== contribution?.id && (
                <div className="relative flex-shrink-0 overflow-visible">
                  <button
                    type="button"
                    onClick={() => setShowMenu(!showMenu)}
                    className="p-1.5 hover:bg-[rgba(197,160,89,0.1)] rounded-lg transition-colors"
                    aria-label="خيارات التعليق"
                  >
                    <Icon name="MoreVertical" size={18} color="#999" />
                  </button>
                  {showMenu && (
                    <div className="absolute left-0 top-full mt-2 bg-white rounded-lg py-1.5 min-w-[120px] z-50 shadow-warm border border-[rgba(197,160,89,0.15)]">
                      {onEditComment && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowMenu(false);
                            setEditingId(contribution?.id);
                            setEditText(contribution?.text || '');
                          }}
                          className="w-full px-4 py-2 text-right text-sm hover:bg-[rgba(197,160,89,0.08)] transition-colors flex items-center gap-2"
                          style={{ fontFamily: 'Amiri, serif', color: '#2C2B29' }}
                        >
                          <Icon name="Edit" size={16} color="#5A5A5A" />
                          <span>تعديل</span>
                        </button>
                      )}
                      {onDeleteComment && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowMenu(false);
                            onDeleteComment?.(contribution?.id);
                          }}
                          className="w-full px-4 py-2 text-right text-sm hover:bg-[rgba(220,38,38,0.08)] transition-colors flex items-center gap-2 text-red-600"
                          style={{ fontFamily: 'Amiri, serif' }}
                        >
                          <Icon name="Trash2" size={16} color="currentColor" />
                          <span>حذف</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Reply Box */}
            {showReplyBox && (
              <div className="mt-4">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e?.target?.value)}
                  placeholder="اكتب ردك هنا..."
                  className="w-full p-3 rounded-xl bg-[#F9F7F2] border border-[rgba(197,160,89,0.3)] focus:outline-none focus:border-[rgba(197,160,89,0.6)] focus:shadow-[0_4px_12px_rgba(197,160,89,0.1)] transition-all duration-300 resize-none"
                  style={{
                    fontFamily: 'Amiri, serif',
                    fontSize: '16px',
                    minHeight: '100px',
                    direction: 'rtl'
                  }}
                />
                <div className="flex items-center justify-end gap-2 mt-2">
                  <button
                    onClick={() => setShowReplyBox(false)}
                    className="px-4 py-2 text-sm rounded-full transition-colors duration-300 hover:bg-[rgba(197,160,89,0.1)]"
                    style={{ fontFamily: 'Amiri, serif', color: '#999' }}
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleReplySubmit}
                    className="px-6 py-2 text-sm rounded-full bg-[#C5A059] text-white hover:bg-[#A68548] transition-colors duration-300"
                    style={{ fontFamily: 'Amiri, serif' }}
                  >
                    نشر
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Nested Replies */}
        {contribution?.replies?.length > 0 && (
          <div className="mt-6">
            {contribution?.replies?.map((reply) => (
              <ContributionItem
                key={reply?.id}
                contribution={reply}
                depth={depth + 1}
                onReply={onReply}
                onLikeComment={onLikeComment}
                isAdmin={isAdmin}
                currentUserId={currentUserId}
                isAuthenticated={isAuthenticated}
                onDeleteComment={onDeleteComment}
                onEditComment={onEditComment}
                onUserClick={onUserClick}
                onSignupRedirect={onSignupRedirect}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ConversationStream = ({
  contributions,
  onReply,
  onLikeComment,
  isAdmin,
  currentUserId,
  isAuthenticated,
  onDeleteComment,
  onEditComment,
  onUserClick,
  onSignupRedirect
}) => {
  return (
    <div className="space-y-6">
      {contributions?.map((contribution) => (
        <ContributionItem
          key={contribution?.id}
          contribution={contribution}
          depth={0}
          onReply={onReply}
          onLikeComment={onLikeComment}
          isAdmin={isAdmin}
          currentUserId={currentUserId}
          isAuthenticated={isAuthenticated}
          onDeleteComment={onDeleteComment}
          onEditComment={onEditComment}
          onUserClick={onUserClick}
          onSignupRedirect={onSignupRedirect}
        />
      ))}
    </div>
  );
};

export default ConversationStream;